import { useEffect, useState } from 'react';

import { useDeleteDocument } from '@/features/Documents/api';
import { useDocuments } from '@/features/end-of-lease/api';
import { DocumentCard } from '@/features/end-of-lease/document-card';
import { DocumentsExplorer } from '@/features/end-of-lease/documents-explorer';
import type { LeasingDocs } from '@/models/docs';
import { createFileRoute } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';

import { parseOptionalPage } from '@/lib/urlUtils';

import { usePagination } from '@/hooks/usePagination';
import { useSearchParamState } from '@/hooks/useSearchParamState';

interface AircraftFolderSearch {
    page?: number;
}

export const Route = createFileRoute(
    '/lease/projects/$projectId/aircraft-folder',
)({
    component: ProjectAircraftFolderPage,
    validateSearch: (search: Record<string, unknown>): AircraftFolderSearch => ({
        page: parseOptionalPage(search.page),
    }),
});

const LIMIT = 4 * 5;

export function ProjectAircraftFolderPage() {
    const { projectId: _projectId } = Route.useParams();
    const projectId = Number(_projectId);

    const [totalElements, setTotalElements] = useState(0);
    const [selectedDocument, setSelectedDocument] =
        useState<LeasingDocs | null>(null);
    const { value: page, setValue: setPage } = useSearchParamState(
        'page',
        parseOptionalPage,
    );
    const { limit, skip, Pagination } = usePagination({
        totalElements,
        limit: LIMIT,
        page,
        onPageChange: setPage,
    });

    const deleteDocument = useDeleteDocument();
    const { data: documents } = useDocuments({
        type: 'ServiceBulletin',
        projectId,
        limit,
        skip,
    });

    useEffect(() => {
        if (documents) setTotalElements(documents.total);
    }, [documents]);

    return (
        <div
            className="flex-1 bg-white min-h-full"
            data-testid="LeaseProjectAircraftFolder"
        >
            <div className="flex flex-col items-center w-full pt-16">
                <h3 className="font-bold text-xl">Aircraft Folder</h3>
                <div className="max-w-xl text-zinc-500 text-sm">
                    The aircraft folder is a comprehensive collection of all
                    essential documents detailing an aircraft’s history,
                    maintenance, modifications, and compliance records. It
                    ensures a smooth end-of-lease transition by proving the
                    aircraft’s airworthiness, regulatory compliance, and asset
                    value.
                </div>
            </div>

            <main className="flex flex-1 overflow-hidden flex-col">
                <div className="flex flex-col gap-2">
                    <div className="flex flex-row">
                        <div className="flex flex-col flex-1">
                            <div className="flex-1 flex-col p-8 gap-8">
                                <DocumentsExplorer
                                    documents={documents?.results || []}
                                    onSelectDocument={setSelectedDocument}
                                    onDeleteDocument={async (doc) =>
                                        await deleteDocument.mutateAsync(doc.id)
                                    }
                                    selectedDocumentId={selectedDocument?.id}
                                />

                                <div className="pt-8">
                                    <Pagination />
                                </div>
                            </div>
                        </div>

                        <AnimatePresence>
                            {selectedDocument && selectedDocument.meta?.raw && (
                                <motion.div
                                    className="w-1/4 border-l"
                                    initial={{ x: '100%', opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: '100%', opacity: 0 }}
                                    transition={{
                                        type: 'tween',
                                        ease: 'easeOut',
                                        duration: 0.2,
                                    }}
                                >
                                    <DocumentCard
                                        id={selectedDocument.id}
                                        leasing_document={selectedDocument}
                                        onClose={() =>
                                            setSelectedDocument(null)
                                        }
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </main>
        </div>
    );
}
